/**
 * Chat Dock Context — "chat docks to header"
 *
 * Owns the lifecycle of the desktop split-view chat panel docking into the
 * app header as a launcher icon. Shared between MainLayout (which renders
 * HeaderChatLauncher in the header action group) and ChatBox (which renders
 * the panel, the X close button and the flight surface), so both sides of
 * the shared-element morph read one source of truth.
 *
 * State machine (a bare boolean can't sequence the choreography):
 *
 *   'open' --dockChat()--> 'docking' --(content fade ~110ms)--> 'docked'
 *   'docked' --undockChat()--> 'undocking' --(flight lands)--> 'open'
 *
 * - 'docking'/'undocking' are transitional flight phases; both carry fallback
 *   timers so a missed animation callback can never wedge the UI.
 * - Persists to the pre-existing localStorage key `xavior:chat-pane-collapsed`
 *   (the old chevron-collapse key) so users who had the pane collapsed load
 *   straight into 'docked' — with no flight, no landing effects and no focus
 *   steal (`pendingLandingFx` is only raised by user-initiated docks).
 * - Under prefers-reduced-motion the transitional phases are skipped entirely
 *   and the panel/launcher swap with a simple fade.
 * - `expandChatPane()` is the programmatic open (agent ui-actions, onboarding):
 *   it jumps straight to 'open' with no animation, matching the legacy
 *   `setIsChatPaneCollapsed(false)` behavior it replaces.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMediaQuery } from '@/common/hooks/useMediaQuery';
import {
  CHAT_DOCK_CONTENT_FADE_MS,
  CHAT_DOCK_UNDOCK_LAND_MS,
  type DockRect,
} from '@/common/motion/chatDockMotion';

/** Pre-existing key (was the chevron collapse pref) — kept for backward compat. */
export const CHAT_PANE_COLLAPSED_STORAGE_KEY = 'xavior:chat-pane-collapsed';

export type ChatDockState = 'open' | 'docking' | 'docked' | 'undocking';

interface ChatDockContextValue {
  dockState: ChatDockState;
  /** True while the desktop split view (module fullscreen, desktop ≥768px) is live — published by ChatBox. */
  isSplitViewActive: boolean;
  /**
   * True while a module pane is open (fullscreen-active) at ANY width — published
   * by ChatBox. With the split live at all desktop widths (strict two-tier model,
   * ≥768px), "module open but split inactive" only occurs on mobile or in
   * transient windows — the header "open chat" button keys off it (and
   * self-hides on mobile).
   */
  isModulePaneOpen: boolean;
  /** Raised by a user dock; consumed by HeaderChatLauncher (flight + pulse + coachmark + focus). */
  pendingLandingFx: boolean;
  /** Raised by a user undock; consumed by ChatBox (focus the chat input on arrival). */
  pendingOpenFocus: boolean;
  /**
   * Raised in the same commit the undock flight lands ('undocking' → 'open'),
   * so ChatBox can wear `.chat-dock-landing` from the very first 'open' frame
   * (a frame-late class would let the default slide-in reveal flash first).
   * ChatBox acks it once the landing reveal has played out.
   */
  pendingLandingReveal: boolean;
  /**
   * Resting rect of the chat column for the current flight (set synchronously
   * when a transition starts so the flight surface mounts in the same commit
   * the launcher/panel swaps — the shared-element FLIP depends on that).
   */
  flightRect: DockRect | null;
  /** User clicked the panel X (or pressed Escape in the panel): animate panel → header. */
  dockChat: (flightRect?: DockRect | null) => void;
  /** User clicked the header launcher: animate header → panel. */
  undockChat: () => void;
  /**
   * ChatBox registers a synchronous "where will the chat column be?"
   * measurer here; undockChat() calls it before flipping state. With no
   * provider (or a null rect) the undock opens instantly instead of
   * entering a flight that could never land.
   */
  registerUndockRectProvider: (provider: (() => DockRect | null) | null) => void;
  /** Programmatic open (agent/onboarding): jump to 'open' instantly. */
  expandChatPane: () => void;
  /** Called by the flight surface when the undock flight lands. */
  finishUndock: () => void;
  ackLandingFx: () => void;
  ackOpenFocus: () => void;
  ackLandingReveal: () => void;
  setSplitViewActive: (active: boolean) => void;
  setModulePaneOpen: (active: boolean) => void;
}

const ChatDockContext = createContext<ChatDockContextValue | undefined>(undefined);

function readPersistedDocked(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(CHAT_PANE_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persistDocked(docked: boolean): void {
  try {
    window.localStorage.setItem(CHAT_PANE_COLLAPSED_STORAGE_KEY, docked ? '1' : '0');
  } catch {
    /* best-effort */
  }
}

export function ChatDockProvider({ children }: { children: ReactNode }) {
  const [dockState, setDockState] = useState<ChatDockState>(() =>
    readPersistedDocked() ? 'docked' : 'open',
  );
  const [isSplitViewActive, setIsSplitViewActiveState] = useState(false);
  const [isModulePaneOpen, setIsModulePaneOpenState] = useState(false);
  const [pendingLandingFx, setPendingLandingFx] = useState(false);
  const [pendingOpenFocus, setPendingOpenFocus] = useState(false);
  const [pendingLandingReveal, setPendingLandingReveal] = useState(false);
  const [flightRect, setFlightRect] = useState<DockRect | null>(null);
  const undockRectProviderRef = useRef<(() => DockRect | null) | null>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Eagerly-updated mirror so the transition guards are airtight against
  // double-clicks landing in the same tick (state updates are async).
  const dockStateRef = useRef(dockState);
  const prefersReducedMotionRef = useRef(prefersReducedMotion);
  useEffect(() => {
    prefersReducedMotionRef.current = prefersReducedMotion;
  }, [prefersReducedMotion]);

  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);
  useEffect(() => clearSettleTimer, [clearSettleTimer]);

  const transition = useCallback((next: ChatDockState) => {
    dockStateRef.current = next;
    setDockState(next);
  }, []);

  const dockChat = useCallback((rect?: DockRect | null) => {
    if (dockStateRef.current !== 'open') return;
    persistDocked(true);
    setPendingLandingFx(true);
    setPendingLandingReveal(false);
    setFlightRect(rect ?? null);
    clearSettleTimer();
    if (prefersReducedMotionRef.current) {
      transition('docked');
      return;
    }
    transition('docking');
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      transition('docked');
    }, CHAT_DOCK_CONTENT_FADE_MS);
  }, [clearSettleTimer, transition]);

  const undockChat = useCallback(() => {
    if (dockStateRef.current !== 'docked') return;
    persistDocked(false);
    setPendingOpenFocus(true);
    clearSettleTimer();
    // Measure the landing rect BEFORE flipping state so the flight surface
    // mounts in the same commit the launcher unmounts — framer-motion needs
    // the launcher's layout snapshot alive for the shared-element FLIP.
    const rect = prefersReducedMotionRef.current
      ? null
      : undockRectProviderRef.current?.() ?? null;
    setFlightRect(rect);
    if (rect === null) {
      // No flight possible (reduced motion, or nothing to measure) — open
      // instantly rather than entering a phase that could never land.
      transition('open');
      return;
    }
    transition('undocking');
    // Deliberately the PRIMARY landing trigger, not just a fallback: framer's
    // rest detection (onLayoutAnimationComplete → finishUndock) lags the
    // visually-settled box by 100-300ms, which read as the glass "lingering"
    // before the real content appeared. Whichever fires first wins.
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      setPendingLandingReveal(true);
      transition('open');
    }, CHAT_DOCK_UNDOCK_LAND_MS);
  }, [clearSettleTimer, transition]);

  const registerUndockRectProvider = useCallback(
    (provider: (() => DockRect | null) | null) => {
      undockRectProviderRef.current = provider;
    },
    [],
  );

  const finishUndock = useCallback(() => {
    if (dockStateRef.current !== 'undocking') return;
    clearSettleTimer();
    // Same-batch with the state flip: ChatBox must wear .chat-dock-landing on
    // the very first 'open' frame or the default slide-in reveal flashes.
    setPendingLandingReveal(true);
    transition('open');
  }, [clearSettleTimer, transition]);

  const expandChatPane = useCallback(() => {
    if (dockStateRef.current === 'open') return;
    clearSettleTimer();
    persistDocked(false);
    setPendingLandingFx(false);
    transition('open');
  }, [clearSettleTimer, transition]);

  const ackLandingFx = useCallback(() => setPendingLandingFx(false), []);
  const ackOpenFocus = useCallback(() => setPendingOpenFocus(false), []);
  const ackLandingReveal = useCallback(() => setPendingLandingReveal(false), []);

  const setSplitViewActive = useCallback((active: boolean) => {
    setIsSplitViewActiveState(active);
    // Leaving the split view orphans any pending one-shot effects (their
    // consumers unmounted); drop them so they can't replay later. The settle
    // timer would be orphaned too — ChatBox can unmount mid-flight
    // (conversation deleted/switched) and the timer would later fire into a
    // void, re-raising pendingLandingReveal for a mount that never flew.
    // Settle any in-flight transition synchronously instead.
    if (!active) {
      setPendingLandingFx(false);
      setPendingOpenFocus(false);
      setPendingLandingReveal(false);
      if (dockStateRef.current === 'docking' || dockStateRef.current === 'undocking') {
        clearSettleTimer();
        transition(dockStateRef.current === 'docking' ? 'docked' : 'open');
      }
    }
  }, [clearSettleTimer, transition]);

  const setModulePaneOpen = useCallback((active: boolean) => {
    setIsModulePaneOpenState(active);
  }, []);

  const value = useMemo<ChatDockContextValue>(
    () => ({
      dockState,
      isSplitViewActive,
      isModulePaneOpen,
      pendingLandingFx,
      pendingOpenFocus,
      pendingLandingReveal,
      flightRect,
      dockChat,
      undockChat,
      expandChatPane,
      finishUndock,
      ackLandingFx,
      ackOpenFocus,
      ackLandingReveal,
      setSplitViewActive,
      setModulePaneOpen,
      registerUndockRectProvider,
    }),
    [
      dockState,
      isSplitViewActive,
      isModulePaneOpen,
      pendingLandingFx,
      pendingOpenFocus,
      pendingLandingReveal,
      flightRect,
      dockChat,
      undockChat,
      expandChatPane,
      finishUndock,
      ackLandingFx,
      ackOpenFocus,
      ackLandingReveal,
      setSplitViewActive,
      setModulePaneOpen,
      registerUndockRectProvider,
    ],
  );

  return <ChatDockContext.Provider value={value}>{children}</ChatDockContext.Provider>;
}

export function useChatDock() {
  const context = useContext(ChatDockContext);
  if (!context) {
    throw new Error('useChatDock must be used within a ChatDockProvider');
  }
  return context;
}
